/**
 * O papel timbrado do editor é o timbrado do app — o mesmo que o PDF aplica
 * (Configurações → Timbrado). Cadastra-se uma vez e vale nos dois.
 *
 * - Lê o timbrado RESOLVIDO (com a precedência do escritório), como o PDF.
 * - Se o resolvido vem do escritório (difere do pessoal), o editor o mostra
 *   como somente leitura: quem o muda é quem administra o escritório.
 * - Senão, salvar no editor grava o timbrado pessoal (cabeçalho, rodapé, logo).
 * - Na conta de outra pessoa (delegação) não há timbrado no editor — mesma regra
 *   da "casca" do Peticionar: o PDF segue com o do titular.
 */
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import {
  fetchTimbrado,
  fetchTimbradoResolvido,
  removeTimbradoLogo,
  saveTimbrado,
  saveTimbradoImagens,
  saveTimbradoPagina,
  uploadTimbradoLogo,
  type AlvoDoTimbrado,
  type TimbradoConfig,
} from '@/api/timbrado';
import type { PaginaPadrao, Timbrado } from '@/components/editor-forense';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import { queryKeys, queryTimings } from '@/lib/pwa/queryConfig';

const ID = 'jurisapp';

/** A chave do cache do timbrado guardado de um alvo (o pessoal ou o de um escritório). */
export const chaveDoTimbrado = (alvo: AlvoDoTimbrado): readonly unknown[] =>
  alvo === 'me' ? [...queryKeys.timbrado.me] : [...queryKeys.timbrado.escritorio(alvo.escritorio)];

/** O timbrado guardado no servidor como o editor o vê (`null` quando não há nada). */
export function timbradoDoServidor(c: TimbradoConfig, doEscritorio = false, prevalece = true): Timbrado | null {
  const cabecalho = c.header?.trim() ?? '';
  const rodape = c.footer?.trim() ?? '';
  const imagemCabecalho = c.header_image_url ?? undefined;
  const imagemRodape = c.footer_image_url ?? undefined;
  const marcaDagua = c.watermark?.trim() || undefined;
  // Só a marca d'água já é timbrado: o PDF a aplica.
  if (!cabecalho && !rodape && !c.logo_url && !imagemCabecalho && !imagemRodape && !marcaDagua) return null;
  return {
    id: ID,
    rotulo: doEscritorio ? 'Timbrado do escritório' : 'Meu timbrado',
    // Com uma faixa pronta (o desenho do papel), ela vai inteira para a folha.
    modo: imagemCabecalho || imagemRodape ? 'imagem' : 'texto',
    cabecalhoTexto: cabecalho,
    rodapeTexto: rodape,
    imagemCabecalho,
    imagemRodape,
    logotipo: c.logo_url ?? undefined,
    marcaDagua,
    // Com o padrão do escritório sugerido, o dele vale até a pessoa ter o seu: salvar cria o seu.
    somenteLeitura: doEscritorio && prevalece,
    aviso: doEscritorio
      ? prevalece
        ? 'É o timbrado do escritório — o mesmo que sai no PDF. Quem administra o escritório o altera em Configurações → Timbrado.'
        : 'É o timbrado que o escritório sugere. Salvar aqui cria o seu, que passa a valer nas suas peças.'
      : undefined,
  };
}

/** O texto que o servidor guarda do cabeçalho e do rodapé (as faixas vão à parte). */
function paraConfig(t: Timbrado): { header: string; footer: string } {
  if (t.modo !== 'dados') return { header: t.cabecalhoTexto ?? '', footer: t.rodapeTexto ?? '' };
  return {
    header: [t.nome, t.oab, t.endereco].filter(Boolean).join('\n'),
    footer: t.contato ?? '',
  };
}

// Sem `fetch(dataUrl)`: a CSP de PROD não tem `data:` em connect-src, e o
// logotipo falhava depois de o cabeçalho e o rodapé já estarem gravados.
function arquivoDoDataUrl(dataUrl: string): File {
  const [cabecalho, base64 = ''] = dataUrl.split(',', 2);
  const tipo = /^data:([^;,]+)/.exec(cabecalho)?.[1] ?? 'image/png';
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  const ext = (tipo.split('/')[1] || 'png').replace('jpeg', 'jpg');
  return new File([bytes], `logo.${ext}`, { type: tipo });
}

/**
 * Grava o timbrado pessoal inteiro — o do editor e o de Configurações → Papel
 * timbrado passam por aqui. O `t` é o timbrado todo: o que não vem nele sai do
 * servidor; compara com o guardado AGORA (o cache), não o de quando a tela foi
 * desenhada (o "Desfazer" de um aviso chama a gravação de antes). A marca d'água
 * não é do editor: sem `marcaDagua`, fica a guardada.
 */
export async function gravarTimbrado(
  queryClient: QueryClient,
  t: Timbrado,
  { marcaDagua, alvo = 'me' }: { marcaDagua?: string; alvo?: AlvoDoTimbrado } = {},
): Promise<TimbradoConfig> {
  const chave = chaveDoTimbrado(alvo);
  const atual = queryClient.getQueryData<TimbradoConfig>(chave);
  // As faixas primeiro: a que o servidor recusa (formato, tamanho) não deixa o
  // resto gravado pela metade. As do modo imagem; fora dele, nenhuma.
  const faixas: { cabecalho?: string | null; rodape?: string | null } = {};
  const cabecalho = t.modo === 'imagem' ? (t.imagemCabecalho ?? null) : null;
  const rodape = t.modo === 'imagem' ? (t.imagemRodape ?? null) : null;
  if (cabecalho !== (atual?.header_image_url ?? null)) faixas.cabecalho = cabecalho;
  if (rodape !== (atual?.footer_image_url ?? null)) faixas.rodape = rodape;
  if (Object.keys(faixas).length) await saveTimbradoImagens(faixas, alvo);
  let salvo = await saveTimbrado({ ...paraConfig(t), watermark: marcaDagua ?? t.marcaDagua ?? atual?.watermark ?? '' }, alvo);
  if (t.logotipo?.startsWith('data:') && t.logotipo !== atual?.logo_url) {
    salvo = await uploadTimbradoLogo(arquivoDoDataUrl(t.logotipo), alvo);
  } else if (!t.logotipo && atual?.logo_url && salvo.has_logo) {
    // Só o logo que a pessoa VIU sair (o timbrado o tinha e não tem mais): sem
    // a prévia (download falhou), não se sabe — e apagar seria perder o arquivo.
    salvo = await removeTimbradoLogo(alvo);
  }
  // A resposta já é o timbrado novo: entra na tela sem esperar outra consulta.
  queryClient.setQueryData(chave, salvo);
  await queryClient.invalidateQueries({ queryKey: chave });
  return salvo;
}

/**
 * Os timbrados do editor e o padrão de uma peça nova. O documento da peça (com o
 * timbrado escolhido) mora no rascunho; salvar um timbrado aqui o liga na peça
 * por `aoUsarTimbrado`.
 */
export function useTimbradoDoEditor({ aoUsarTimbrado }: { aoUsarTimbrado: (id: string) => void }) {
  const atuacao = useAtuacao();
  const ativo = !atuacao;
  const queryClient = useQueryClient();
  const resolvido = useQuery({
    queryKey: [...queryKeys.timbrado.resolvido],
    queryFn: fetchTimbradoResolvido,
    enabled: ativo,
    ...queryTimings.timbrado,
  });
  const pessoal = useQuery({
    queryKey: [...queryKeys.timbrado.me],
    queryFn: fetchTimbrado,
    enabled: ativo,
    ...queryTimings.timbrado,
  });

  const timbrado = useMemo(() => {
    if (!ativo || !resolvido.data) return null;
    const p = pessoal.data;
    const assinatura = (c: TimbradoConfig) =>
      [c.header, c.footer, c.logo_url, c.header_image_url, c.footer_image_url, c.watermark].map((v) => v ?? '').join('|');
    const doEscritorio = !!p && assinatura(p) !== assinatura(resolvido.data);
    return timbradoDoServidor(resolvido.data, doEscritorio, resolvido.data.escritorio_prevalece !== false);
  }, [ativo, resolvido.data, pessoal.data]);

  const onTimbradoSalvo = async (t: Timbrado) => {
    if (!ativo || t.somenteLeitura) return;
    try {
      const salvo = await gravarTimbrado(queryClient, t);
      // Sem timbrado do escritório por cima, o pessoal é o que o PDF aplica.
      if (!timbrado?.somenteLeitura) queryClient.setQueryData([...queryKeys.timbrado.resolvido], salvo);
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.timbrado.resolvido] });
      // O servidor guarda um timbrado só: a peça passa a apontar para ele.
      aoUsarTimbrado(ID);
      toast.success('Timbrado salvo. Vale no editor e no PDF.');
    } catch {
      toast.error('Não foi possível salvar o timbrado. Tente de novo em Configurações → Timbrado.');
    }
  };

  /**
   * A página das peças novas (D12): mora com o timbrado e tem a mesma precedência
   * (a do escritório, quando houver). Gravar mexe só na pessoal; a peça aberta
   * não muda.
   */
  const paginaPadrao: PaginaPadrao | null = (ativo && resolvido.data?.pagina) || null;
  const onPaginaPadrao = async (p: PaginaPadrao | null) => {
    try {
      const salvo = await saveTimbradoPagina(p);
      queryClient.setQueryData([...queryKeys.timbrado.me], salvo);
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.timbrado.resolvido] });
    } catch {
      toast.error('Não foi possível guardar a página padrão. Tente de novo.');
      throw new Error('pagina-padrao');
    }
  };

  // Referência estável: o painel do timbrado zera o formulário quando a lista
  // muda, e um array novo a cada desenho apagava o que a pessoa digitava.
  const timbrados = useMemo(() => (timbrado ? [timbrado] : []), [timbrado]);

  return {
    timbrados,
    // Na conta de outra pessoa não se grava timbrado: sem a função, o editor não oferece.
    onTimbradoSalvo: ativo ? onTimbradoSalvo : undefined,
    /**
     * Peça sem documento salvo abre com o timbrado do escritório (o do PDF). Só é
     * "sem timbrado" quando se SABE que não há um: na conta de outra pessoa (o
     * servidor aplica o do titular, que o delegado não vê) e enquanto a consulta
     * não respondeu ou falhou, fica ligado — o servidor resolve, e sem timbrado
     * cadastrado não aplica nada. Desligá-lo ali gravava a peça sem timbrado.
     */
    timbradoPadrao: timbrado || !ativo || !resolvido.isSuccess ? ID : null,
    paginaPadrao,
    // Na conta de outra pessoa a página padrão é a do titular, que o delegado não grava.
    onPaginaPadrao: ativo ? onPaginaPadrao : undefined,
  };
}
